/**
 * YIELD MANAGEMENT COMPONENT - SECURE TOKEN APPROVALS
 *
 * CRITICAL SECURITY IMPROVEMENT:
 * ===============================
 * - ELIMINATED: Dangerous maxUint256 unlimited approvals
 * - IMPLEMENTED: Secure approval strategies with exact amounts
 * - PROTECTION: Contract compromise cannot drain all user funds
 *
 * APPROVAL SECURITY:
 * ==================
 * - EXACT AMOUNT: Approves only what's needed for transaction
 * - BUFFERED: Adds small buffer (5%) for gas/precision safety
 * - VALIDATION: Checks allowance sufficiency before approval
 * - SAFETY: Validates approval amounts against user balance
 *
 * RISK MITIGATION:
 * ================
 * - No unlimited spending permissions
 * - Contract compromise limited to approved amounts
 * - Clear user feedback on approval scope
 * - Audit trail of approval transactions
 *
 * BEFORE (HIGH RISK):
 * - maxUint256 approval = unlimited access forever
 * - Contract hack = total fund loss
 *
 * AFTER (SECURE):
 * - Exact amount + 5% buffer approval
 * - Contract hack = limited to reward amount only
 */

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Calendar,
  DollarSign,
  Loader2,
  Percent,
  Play,
  RefreshCw,
  Settings,
  TrendingUp,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { actions } from '../../actions';
import dataProvider from '../../authentication/dataProvider';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import { useToast } from '../../components/ui/use-toast';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { services } from '../../services';
import { createAdminNotification } from '../../services/notificationService';
import { getUserFriendlyError } from '../../utils/errorMessages';
import {
  calculateSecureApprovalAmount,
  executeSecureApproval,
  getRecommendedApprovalStrategy,
  hasSufficientAllowance,
  validateApprovalSafety,
} from '../../utils/tokenApproval';
import { web3 } from '../../web3';

interface AssetYieldData {
  id: string;
  assetName: string;
  symbol: string;
  rate: number;
  currentPrice: number;
  /** Current USDT sitting in the reward pool awaiting claims (on-chain). Rendered as "Pending". */
  rewardBalance: number;
  assetAddress?: string;
  /** Distinct investor wallets from the Invest collection (DB), sourced from backend admin list. */
  investorCount?: number;
  /** Sum of all historical yield claims for this asset (DB), sourced from backend admin list. */
  distributed?: number;
}

/** Adaptive currency formatter — picks K / M / B / T suffix based on magnitude. */
const formatCurrencyCompact = (amount: number): string => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return '$0';
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

const ensureNumber = (value: any): number => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

// Retry mechanism for contract calls
const callWithRetry = async <T,>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === retries - 1) throw error;

      // Exponential backoff
      const waitTime = delay * Math.pow(2, i);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }
  throw new Error('Max retries exceeded');
};

const UpdateYields = () => {
  const { toast } = useToast();
  const { isOnline } = useNetworkStatus();
  const [loading, setLoading] = useState(true);
  const [blockchainLoading, setBlockchainLoading] = useState(false);
  // True after the first successful on-chain fetch completes. The progress bar stays at 0
  // until this flips — prevents the "fills to 100% then drops" flash caused by computing
  // the ratio before rewardBalance has loaded (when pending=0 the ratio would be 100%).
  const [blockchainReady, setBlockchainReady] = useState(false);
  const [assets, setAssets] = useState<any[]>([]);
  const [assetYieldData, setAssetYieldData] = useState<Record<string, AssetYieldData>>({});
  const [web3Data, setWeb3Data] = useState<any>(null);
  const [tokenContract, setTokenContract] = useState<any>(null);

  // Modal states
  const [yieldModalOpen, setYieldModalOpen] = useState(false);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [rewardModalOpen, setRewardModalOpen] = useState(false);
  const [selectedAssetForModal, setSelectedAssetForModal] = useState<any>(null);

  // Transaction loading states
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [rewardTransactionLoading, setRewardTransactionLoading] = useState(false);
  const [priceTransactionLoading, setPriceTransactionLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Form values
  const [yieldValue, setYieldValue] = useState<string>('');
  const [rewardValue, setRewardValue] = useState<string>('');
  const [priceValue, setPriceValue] = useState<string>('');

  // Initialize Web3 and token contract
  useEffect(() => {
    async function initializeWeb3() {
      try {
        const web3Data = await services.getWeb3();
        if (web3Data && web3Data.accounts && web3Data.accounts[0]) {
          setWeb3Data(web3Data);
          const tokenInstance = await actions.getTokenContract();
          if (tokenInstance) {
            setTokenContract(tokenInstance);
          }
        }
      } catch (error) {
        toast({
          title: 'Wallet Connection',
          description: getUserFriendlyError(error),
          variant: 'destructive',
        });
      }
    }
    initializeWeb3();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  const assetsFetchRetryCount = useRef(0);
  const ASSETS_FETCH_MAX_RETRIES = 1;

  // Fetch assets from API (extracted so Refresh can call it too)
  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dataProvider.getList('assets', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'id', order: 'DESC' },
        filter: {},
      });

      if (Array.isArray(response.data)) {
        setAssets(response.data);
        const initialData: Record<string, AssetYieldData> = {};
        response.data.forEach((asset: any) => {
          if (asset.id) {
            initialData[asset.id] = {
              id: asset.id,
              assetName: asset.assetName || asset.name || 'Unknown Asset',
              symbol: asset.symbol || asset.tokenSymbol || '',
              rate: asset.yieldRate || asset.annualYield || 0,
              currentPrice: asset.assetPrice || 0,
              rewardBalance: 0,
              assetAddress: asset.assetAddress,
              investorCount: asset.totalInvestorsCount || 0,
              distributed: ensureNumber(asset.distributedAmount),
            };
          }
        });
        setAssetYieldData(initialData);
        return response.data;
      } else {
        setAssets([]);
        return [];
      }
    } catch (error) {
      setAssets([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Load assets on mount; retry once if first load returns empty (avoids "no data until refresh")
  useEffect(() => {
    assetsFetchRetryCount.current = 0;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;

    const run = async () => {
      const data = await fetchAssets();
      if (
        Array.isArray(data) &&
        data.length === 0 &&
        assetsFetchRetryCount.current < ASSETS_FETCH_MAX_RETRIES
      ) {
        assetsFetchRetryCount.current += 1;
        retryTimeout = setTimeout(run, 1500);
      }
    };

    run();
    return () => {
      if (retryTimeout !== undefined) clearTimeout(retryTimeout);
    };
  }, [fetchAssets]);

  // Fetch blockchain data for assets - Optimized with parallel calls
  useEffect(() => {
    const fetchBlockchainData = async () => {
      if (!web3Data || !tokenContract || assets.length === 0) return;

      setBlockchainLoading(true);

      try {
        // Prepare all contract calls in parallel
        const contractPromises = assets
          .filter((asset) => asset.assetAddress && asset.id)
          .map(async (asset) => {
            try {
              const contractInstance = await actions.getPropertyAssetContract(asset.assetAddress);
              if (!contractInstance) {
                throw new Error('Contract not found');
              }

              // Bulk fetch: only the fields displayed on the page.
              // - assetPrice() is fetched lazily when the Price modal opens (saves 1 RPC/asset)
              // - getTokenHolders() is not used — investor count comes from the DB (saves 1 RPC/asset and
              //   avoids clobbering the authoritative DB-sourced totalInvestorsCount with a
              //   semantically different on-chain holder count)
              const [yieldData, rewardBalanceData] = await Promise.allSettled([
                callWithRetry(() => contractInstance.methods.assetYield().call(), 2, 500).catch(
                  () => '0'
                ),
                contractInstance.methods.getRewardPoolBalance
                  ? callWithRetry(
                      () => contractInstance.methods.getRewardPoolBalance().call(),
                      2,
                      500
                    ).catch(() => '0')
                  : Promise.resolve('0'),
              ]);

              const yieldNumber = ensureNumber(
                yieldData.status === 'fulfilled' ? yieldData.value : '0'
              );

              let rewardBalance = 0;
              if (rewardBalanceData.status === 'fulfilled') {
                const balance = rewardBalanceData.value;
                let balanceStr = '0';
                if (typeof balance === 'string') {
                  balanceStr = balance;
                } else if (balance && balance.toString) {
                  balanceStr = balance.toString();
                }
                rewardBalance = parseFloat(web3.utils.fromWei(balanceStr, 'ether')) || 0;
              }

              return {
                assetId: asset.id,
                data: {
                  rate: yieldNumber,
                  rewardBalance: rewardBalance,
                },
              };
            } catch (error) {
              return {
                assetId: asset.id,
                data: {
                  rate: 0,
                  rewardBalance: 0,
                },
              };
            }
          });

        // Wait for all contract calls to complete
        const results = await Promise.allSettled(contractPromises);

        // Merge blockchain results into latest state (avoid stale closure)
        setAssetYieldData((prev) => {
          const updatedData = { ...prev };
          results.forEach((result) => {
            if (result.status === 'fulfilled' && result.value) {
              const { assetId, data } = result.value;
              if (updatedData[assetId]) {
                updatedData[assetId] = {
                  ...updatedData[assetId],
                  ...data,
                };
              }
            }
          });
          return updatedData;
        });
      } catch (error) {
        toast({
          title: 'Data Fetch Warning',
          description: getUserFriendlyError(error),
          variant: 'default',
        });
      } finally {
        setBlockchainLoading(false);
        setBlockchainReady(true);
      }
    };

    // Small delay to ensure UI is rendered first
    const timeoutId = setTimeout(fetchBlockchainData, 100);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [web3Data, tokenContract, assets, toast]);

  const fetchRewardPoolBalance = async (assetAddress: string) => {
    try {
      if (!assetAddress) return 0;

      const contractInstance = await actions.getPropertyAssetContract(assetAddress);
      if (!contractInstance || !contractInstance.methods.getRewardPoolBalance) return 0;

      const balance = await contractInstance.methods.getRewardPoolBalance().call();

      // Handle BigNumber or string response
      let balanceStr = '0';
      if (typeof balance === 'string') {
        balanceStr = balance;
      } else if (balance && balance.toString) {
        balanceStr = balance.toString();
      }

      const result = parseFloat(web3.utils.fromWei(balanceStr, 'ether')) || 0;
      return result;
    } catch (error) {
      return 0;
    }
  };

  const openYieldModal = (asset: any) => {
    setSelectedAssetForModal(asset);
    const assetData = assetYieldData[asset.id];
    setYieldValue('');
    setYieldModalOpen(true);
  };

  const openPriceModal = async (asset: any) => {
    setSelectedAssetForModal(asset);
    setPriceValue('');
    setPriceModalOpen(true);

    // Lazy-fetch current on-chain price only when the admin opens the modal.
    // This keeps one assetPrice() RPC call off the bulk refresh path (saves 1 call per asset).
    if (!asset?.assetAddress || !web3Data) return;
    try {
      const contractInstance = await actions.getPropertyAssetContract(asset.assetAddress);
      if (!contractInstance) return;
      const priceInWei = await contractInstance.methods.assetPrice().call();
      const priceStr = typeof priceInWei === 'string' ? priceInWei : String(priceInWei || '0');
      const priceNumber = ensureNumber(web3.utils.fromWei(priceStr, 'ether'));
      setAssetYieldData((prev) => {
        if (!prev[asset.id]) return prev;
        return {
          ...prev,
          [asset.id]: { ...prev[asset.id], currentPrice: priceNumber },
        };
      });
    } catch {
      // Fall back to whatever price is already in state (DB asset.assetPrice seeded at mount).
    }
  };

  const openRewardModal = (asset: any) => {
    setSelectedAssetForModal(asset);
    setRewardValue('');
    setRewardModalOpen(true);
  };

  const updateYield = async () => {
    if (!selectedAssetForModal) return;

    const numericValue = parseFloat(yieldValue);
    if (isNaN(numericValue) || numericValue < 0 || numericValue > 100) {
      toast({
        title: 'Error',
        description: 'Yield must be between 0 and 100',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (!web3Data || !web3Data.accounts[0]) {
        toast({
          title: 'Error',
          description: 'Please connect your wallet first.',
          variant: 'destructive',
        });
        return;
      }

      if (!selectedAssetForModal.assetAddress) {
        toast({
          title: 'Error',
          description: 'Asset contract address not found',
          variant: 'destructive',
        });
        return;
      }

      const contractInstance = await actions.getPropertyAssetContract(
        selectedAssetForModal.assetAddress
      );
      if (!contractInstance) {
        toast({
          title: 'Error',
          description: 'Contract not initialized. Please check your wallet connection.',
          variant: 'destructive',
        });
        return;
      }

      setTransactionLoading(true);

      await contractInstance.methods
        .updateAssetYield(yieldValue)
        .send({ from: web3Data.accounts[0] })
        .on('transactionHash', (hash: string) => {
          toast({
            title: 'Transaction Initiated',
            description: `Yield update transaction submitted. Hash: ${hash.slice(0, 10)}...`,
            variant: 'default',
            duration: 5000,
          });
        })
        .on('receipt', async () => {
          setYieldValue('');
          setYieldModalOpen(false);

          // Refresh asset data
          const newYield = await contractInstance.methods.assetYield().call();
          const updatedData = { ...assetYieldData };
          if (updatedData[selectedAssetForModal.id]) {
            updatedData[selectedAssetForModal.id].rate = Number(newYield);
          }
          setAssetYieldData(updatedData);

          setTransactionLoading(false);
          toast({
            title: 'Success',
            description: `Yield has been successfully updated to ${yieldValue}%`,
            variant: 'default',
            duration: 5000,
          });
          createAdminNotification({
            type: 'yield',
            title: 'Yield updated',
            message: `${selectedAssetForModal?.assetName || 'Asset'} yield set to ${yieldValue}%.`,
            priority: 'medium',
            assetId: selectedAssetForModal?.id,
          });
        })
        .on('error', (error: any) => {
          setTransactionLoading(false);
          toast({
            title: 'Transaction Failed',
            description: error.message || 'Failed to update yield. Please try again.',
            variant: 'destructive',
            duration: 5000,
          });
        });
    } catch (error: any) {
      setTransactionLoading(false);
      toast({
        title: 'Error',
        description:
          'Failed to update yield: ' + (error.message || 'Unknown error. Please try again.'),
        variant: 'destructive',
        duration: 5000,
      });
    }
  };

  const updatePrice = async () => {
    if (!selectedAssetForModal) return;

    const numericValue = parseFloat(priceValue);
    if (isNaN(numericValue) || numericValue <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter a valid positive number',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (!web3Data || !web3Data.accounts[0]) {
        toast({
          title: 'Error',
          description: 'Please connect your wallet first.',
          variant: 'destructive',
        });
        return;
      }

      if (!selectedAssetForModal.assetAddress) {
        toast({
          title: 'Error',
          description: 'Asset contract address not found',
          variant: 'destructive',
        });
        return;
      }

      const contractInstance = await actions.getPropertyAssetContract(
        selectedAssetForModal.assetAddress
      );
      if (!contractInstance) {
        toast({
          title: 'Error',
          description: 'Contract not initialized. Please check your wallet connection.',
          variant: 'destructive',
        });
        return;
      }

      setPriceTransactionLoading(true);
      const priceInWei = web3.utils.toWei(priceValue.toString(), 'ether');

      await contractInstance.methods
        .updateAssetPrice(priceInWei)
        .send({ from: web3Data.accounts[0] })
        .on('transactionHash', (hash: string) => {
          toast({
            title: 'Transaction Initiated',
            description: `Price update transaction submitted. Hash: ${hash.slice(0, 10)}...`,
            variant: 'default',
            duration: 5000,
          });
        })
        .on('receipt', async () => {
          setPriceValue('');
          setPriceModalOpen(false);

          // Refresh asset data
          const newPrice = await contractInstance.methods.assetPrice().call();
          const updatedData = { ...assetYieldData };
          if (updatedData[selectedAssetForModal.id]) {
            const priceInWei = typeof newPrice === 'string' ? newPrice : String(newPrice || '0');
            updatedData[selectedAssetForModal.id].currentPrice = ensureNumber(
              web3.utils.fromWei(priceInWei, 'ether')
            );
          }
          setAssetYieldData(updatedData);

          setPriceTransactionLoading(false);
          toast({
            title: 'Success',
            description: `Price has been successfully updated to ${priceValue} USDT`,
            variant: 'default',
            duration: 5000,
          });
          createAdminNotification({
            type: 'yield',
            title: 'Asset price updated',
            message: `${selectedAssetForModal?.assetName || 'Asset'} price set to ${priceValue} USDT.`,
            priority: 'medium',
            assetId: selectedAssetForModal?.id,
          });
        })
        .on('error', (error: any) => {
          setPriceTransactionLoading(false);
          toast({
            title: 'Transaction Failed',
            description: error.message || 'Failed to update price. Please try again.',
            variant: 'destructive',
            duration: 5000,
          });
        });
    } catch (error: any) {
      setPriceTransactionLoading(false);
      toast({
        title: 'Error',
        description:
          'Failed to update price: ' + (error.message || 'Unknown error. Please try again.'),
        variant: 'destructive',
        duration: 5000,
      });
    }
  };

  const addReward = async () => {
    if (!selectedAssetForModal) return;

    const numericValue = parseFloat(rewardValue);
    if (isNaN(numericValue) || numericValue <= 0) {
      toast({
        title: 'Error',
        variant: 'destructive',
        description: 'Please enter a valid positive number',
      });
      return;
    }

    try {
      if (!web3Data || !web3Data.accounts[0]) {
        toast({
          title: 'Error',
          variant: 'destructive',
          description: 'Please connect your wallet first.',
        });
        return;
      }

      if (!tokenContract || !selectedAssetForModal.assetAddress) {
        toast({
          title: 'Error',
          variant: 'destructive',
          description: 'Contracts not initialized. Please check your wallet connection.',
        });
        return;
      }

      setRewardTransactionLoading(true);
      const amountInWei = web3.utils.toWei(rewardValue.toString(), 'ether');

      const contractInstance = await actions.getPropertyAssetContract(
        selectedAssetForModal.assetAddress
      );
      if (!contractInstance) {
        toast({
          title: 'Error',
          variant: 'destructive',
          description: 'Asset contract not found',
        });
        setRewardTransactionLoading(false);
        return;
      }

      /**
       * SECURE TOKEN APPROVAL - CRITICAL SECURITY IMPROVEMENT
       *
       * BEFORE: maxUint256 (unlimited approval) - EXTREME RISK
       * NOW: Exact amount + buffer - SECURE & LIMITED
       *
       * Why this matters:
       * - Contract compromise = limited fund loss instead of total drain
       * - No permanent unlimited permissions
       * - Clear scope of approved spending
       * - Gas-efficient exact approvals
       */
      const hasAllowance = await hasSufficientAllowance(
        tokenContract,
        web3Data.accounts[0],
        selectedAssetForModal.assetAddress,
        amountInWei
      );

      if (!hasAllowance) {
        // Use recommended strategy for reward transactions (buffered for gas safety)
        const approvalStrategy = getRecommendedApprovalStrategy('reward');
        const approvalOptions = {
          strategy: approvalStrategy,
          bufferPercentage: 5, // Small 5% buffer for gas fees
          requireUserConfirmation: false, // Don't show confirmation for low-risk buffered approval
        };

        // Validate approval safety
        const { amount: approvalAmount } = calculateSecureApprovalAmount(
          amountInWei,
          approvalOptions
        );
        const userBalance = await tokenContract.methods.balanceOf(web3Data.accounts[0]).call();

        const safetyCheck = validateApprovalSafety(approvalAmount, userBalance, amountInWei);
        if (!safetyCheck.isSafe) {
          // Still proceed but log warnings
        }

        toast({
          title: 'Secure Approval Required',
          description: `Approving exact reward amount + 5% buffer for gas safety (no unlimited access)`,
          variant: 'default',
          duration: 5000,
        });

        await executeSecureApproval(
          tokenContract,
          selectedAssetForModal.assetAddress,
          amountInWei,
          approvalOptions,
          web3Data.accounts[0]
        )
          .on('transactionHash', (hash: string) => {
            toast({
              title: 'Secure Approval Pending',
              description: `Approval transaction submitted. Hash: ${hash.slice(0, 10)}...`,
              variant: 'default',
              duration: 5000,
            });
          })
          .on('receipt', () => {
            toast({
              title: 'Secure Approval Success',
              variant: 'default',
              description: 'Token approval successful. Processing reward...',
              duration: 5000,
            });
          })
          .on('error', (error: any) => {
            setRewardTransactionLoading(false);
            throw new Error(error.message || 'Failed to approve tokens securely');
          });
      }

      await contractInstance.methods
        .addReward(amountInWei)
        .send({ from: web3Data.accounts[0] })
        .on('transactionHash', (hash: string) => {
          toast({
            title: 'Transaction Initiated',
            description: `Reward transaction submitted. Hash: ${hash.slice(0, 10)}...`,
            variant: 'default',
            duration: 5000,
          });
        })
        .on('receipt', async () => {
          setRewardValue('');
          setRewardModalOpen(false);

          // Refresh reward balance
          const newBalance = await fetchRewardPoolBalance(selectedAssetForModal.assetAddress);
          const updatedData = { ...assetYieldData };
          if (updatedData[selectedAssetForModal.id]) {
            updatedData[selectedAssetForModal.id].rewardBalance = newBalance;
          }
          setAssetYieldData(updatedData);

          setRewardTransactionLoading(false);
          toast({
            title: 'Success',
            variant: 'default',
            description: `Successfully added ${rewardValue} USDT as reward`,
            duration: 5000,
          });
          createAdminNotification({
            type: 'yield',
            title: 'Reward added',
            message: `${rewardValue} USDT reward added to ${selectedAssetForModal?.assetName || 'asset'}.`,
            priority: 'medium',
            assetId: selectedAssetForModal?.id,
          });
        })
        .on('error', (error: any) => {
          setRewardTransactionLoading(false);
          toast({
            title: 'Transaction Failed',
            variant: 'destructive',
            description: error.message || 'Failed to add reward',
            duration: 5000,
          });
        });
    } catch (error: any) {
      setRewardTransactionLoading(false);
      toast({
        title: 'Error',
        variant: 'destructive',
        description: 'Failed to add reward: ' + (error.message || 'Unknown error'),
        duration: 5000,
      });
    }
  };

  const refreshBlockchainData = async (overrideAssets?: any[]) => {
    const assetsToUse = overrideAssets ?? assets;
    if (!web3Data || !tokenContract || assetsToUse.length === 0) return;

    setRefreshing(true);
    setBlockchainLoading(true);
    // Reset so the progress bar drops to 0 and re-animates up to the new value once the
    // refreshed on-chain reward balances arrive. Matches the "start from zero on refresh" UX.
    setBlockchainReady(false);

    try {
      const contractPromises = assetsToUse
        .filter((asset) => asset.assetAddress && asset.id)
        .map(async (asset) => {
          try {
            const contractInstance = await actions.getPropertyAssetContract(asset.assetAddress);
            if (!contractInstance) {
              throw new Error('Contract not found');
            }

            // Same rationale as the mount-time bulk fetch: only yield + reward pool are
            // fetched eagerly. Price is lazy-loaded on modal open; investor count is
            // served by the backend.
            const [yieldData, rewardBalanceData] = await Promise.allSettled([
              callWithRetry(() => contractInstance.methods.assetYield().call(), 2, 500).catch(
                () => '0'
              ),
              contractInstance.methods.getRewardPoolBalance
                ? callWithRetry(
                    () => contractInstance.methods.getRewardPoolBalance().call(),
                    2,
                    500
                  ).catch(() => '0')
                : Promise.resolve('0'),
            ]);

            const yieldNumber = ensureNumber(
              yieldData.status === 'fulfilled' ? yieldData.value : '0'
            );

            let rewardBalance = 0;
            if (rewardBalanceData.status === 'fulfilled') {
              const balance = rewardBalanceData.value;
              let balanceStr = '0';
              if (typeof balance === 'string') {
                balanceStr = balance;
              } else if (balance && balance.toString) {
                balanceStr = balance.toString();
              }
              rewardBalance = parseFloat(web3.utils.fromWei(balanceStr, 'ether')) || 0;
            }

            return {
              assetId: asset.id,
              data: {
                rate: yieldNumber,
                rewardBalance: rewardBalance,
              },
            };
          } catch (error) {
            return null;
          }
        });

      const results = await Promise.allSettled(contractPromises);

      setAssetYieldData((prev) => {
        const updatedData = { ...prev };
        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value) {
            const { assetId, data } = result.value;
            if (updatedData[assetId]) {
              updatedData[assetId] = {
                ...updatedData[assetId],
                ...data,
              };
            }
          }
        });
        return updatedData;
      });

      toast({
        title: 'Data Refreshed',
        description: 'Blockchain data has been updated with the latest information.',
        variant: 'default',
      });
    } catch (error) {
      toast({
        title: 'Refresh Failed',
        description: getUserFriendlyError(error),
        variant: 'destructive',
      });
    } finally {
      setRefreshing(false);
      setBlockchainLoading(false);
      setBlockchainReady(true);
    }
  };

  // Full refresh: refetch assets then blockchain (fixes "no data until page refresh")
  const handleRefreshData = async () => {
    setRefreshing(true);
    try {
      const newAssets = await fetchAssets();
      await refreshBlockchainData(
        Array.isArray(newAssets) && newAssets.length > 0 ? newAssets : undefined
      );
    } finally {
      setRefreshing(false);
    }
  };

  // Calculate summary statistics
  const assetEntries = Object.values(assetYieldData);
  const avgYieldRate =
    assetEntries.length > 0
      ? assetEntries.reduce((sum, asset) => sum + asset.rate, 0) / assetEntries.length
      : 0;

  // On-chain reward pool balance is what is pending to be claimed
  const totalPending = assetEntries.reduce((sum, asset) => sum + (asset.rewardBalance || 0), 0);
  // Sum of historical yield claims aggregated on the backend
  const totalDistributed = assetEntries.reduce((sum, asset) => sum + (asset.distributed || 0), 0);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <div className='text-center space-y-4'>
          <Loader2 className='h-8 w-8 animate-spin mx-auto' />
          <p className='text-muted-foreground'>Loading assets...</p>
        </div>
      </div>
    );
  }

  const selectedAssetData = selectedAssetForModal ? assetYieldData[selectedAssetForModal.id] : null;

  return (
    <div className='space-y-6 animate-slide-up'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-bold text-foreground'>Yield Distribution</h1>
          <p className='text-muted-foreground'>
            Configure and manage yield distributions for tokenized assets.
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            onClick={handleRefreshData}
            disabled={refreshing || blockchainLoading}
            className='flex items-center gap-2'
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        <div className='glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-success/20 text-success'>
              <DollarSign className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Total Distributed</p>
              <p className='text-2xl font-bold text-foreground'>
                {formatCurrencyCompact(totalDistributed)}
              </p>
            </div>
          </div>
        </div>
        <div className='glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-warning/20 text-warning'>
              <Calendar className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Pending Payout</p>
              {blockchainLoading ? (
                <div className='flex items-center gap-2 mt-1'>
                  <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                  <span className='text-sm text-muted-foreground'>Loading...</span>
                </div>
              ) : (
                <p className='text-2xl font-bold text-foreground'>
                  {formatCurrencyCompact(totalPending)}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className='glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-primary/20 text-primary'>
              <Percent className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Avg. Yield Rate</p>
              <p className='text-2xl font-bold text-foreground'>{avgYieldRate.toFixed(1)}%</p>
            </div>
          </div>
        </div>
        <div className='glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-12 h-12 rounded-xl bg-info/20 text-info'>
              <TrendingUp className='w-6 h-6' />
            </div>
            <div>
              <p className='text-sm text-muted-foreground'>Active Assets</p>
              <p className='text-2xl font-bold text-foreground'>
                {Object.keys(assetYieldData).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Network Status */}
      {!isOnline && (
        <div className='bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center gap-3'>
          <div className='w-2 h-2 rounded-full bg-destructive animate-pulse' />
          <p className='text-sm text-destructive'>
            You are currently offline. Some features may not work properly.
          </p>
        </div>
      )}

      {/* Yields List */}
      <div className='space-y-4'>
        {blockchainLoading && (
          <div className='bg-info/10 border border-info/20 rounded-lg p-4 flex items-center gap-3'>
            <Loader2 className='h-4 w-4 animate-spin text-info' />
            <p className='text-sm text-info'>Fetching live blockchain data...</p>
          </div>
        )}

        {assets.map((asset) => {
          const assetData = assetYieldData[asset.id];
          if (!assetData) return null;

          const distributed = ensureNumber(assetData.distributed);
          const pending = ensureNumber(assetData.rewardBalance);
          // Progress bar: amount distributed out of (distributed + pending). Capped at 100%.
          // Gated on blockchainReady so the bar stays at 0 until pending is known — otherwise
          // during the loading window (distributed>0, pending=0) the ratio would be 100% and
          // then visibly decrease when pending arrives. Starting at 0 lets Progress's
          // `transition-all` animate smoothly up to the real value.
          const progressPercentage =
            blockchainReady && distributed + pending > 0
              ? Math.min(100, (distributed / (distributed + pending)) * 100)
              : 0;

          return (
            <div
              key={asset.id}
              className='glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300'
            >
              <div className='flex flex-col lg:flex-row lg:items-center justify-between gap-6'>
                <div className='flex items-center gap-4'>
                  {assetData.symbol ? (
                    <code className='text-lg font-mono text-primary bg-primary/10 px-4 py-2 rounded-lg border border-primary/20'>
                      ${assetData.symbol}
                    </code>
                  ) : null}
                  <div>
                    <h3 className='font-semibold text-foreground text-lg'>{assetData.assetName}</h3>
                    <p className='text-sm text-muted-foreground'>Asset ID: {asset.id}</p>
                  </div>
                </div>

                <div className='flex flex-wrap items-center gap-8'>
                  <div className='text-center min-w-[80px]'>
                    <p className='text-2xl font-bold text-success'>
                      {ensureNumber(assetData.rate).toFixed(1)}%
                    </p>
                    <p className='text-xs text-muted-foreground'>Annual Yield</p>
                  </div>

                  {/*
                    Price display removed per requirement — admin can still update price via
                    the "Update Price" action in the Configure menu.
                    <div className='text-center min-w-[100px]'>
                      <p className='text-2xl font-bold text-foreground'>
                        ${Number(assetData.currentPrice || 0).toFixed(2)}
                      </p>
                      <p className='text-xs text-muted-foreground'>Price</p>
                    </div>
                  */}

                  <div className='text-center min-w-[100px]'>
                    <p className='text-2xl font-bold text-foreground'>
                      {formatCurrencyCompact(distributed)}
                    </p>
                    <p className='text-xs text-muted-foreground'>Distributed</p>
                  </div>

                  <div className='text-center min-w-[80px]'>
                    {blockchainLoading ? (
                      <div className='flex flex-col items-center'>
                        <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                        <p className='text-xs text-muted-foreground mt-1'>Loading...</p>
                      </div>
                    ) : (
                      <>
                        <p
                          className={`text-2xl font-bold ${pending > 0 ? 'text-warning' : 'text-muted-foreground'}`}
                        >
                          {formatCurrencyCompact(pending)}
                        </p>
                        <p className='text-xs text-muted-foreground'>Pending</p>
                      </>
                    )}
                  </div>

                  {/*
                    Reward Pool display removed per requirement — its value is now surfaced
                    as "Pending" above. Admin can still top up via the "Add Reward" action.
                  */}

                  <div className='text-center min-w-[80px]'>
                    {blockchainLoading ? (
                      <div className='flex flex-col items-center'>
                        <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                        <p className='text-xs text-muted-foreground mt-1'>Loading...</p>
                      </div>
                    ) : (
                      <>
                        <p className='text-2xl font-bold text-foreground'>
                          {assetData.investorCount || 0}
                        </p>
                        <p className='text-xs text-muted-foreground'>Investors</p>
                      </>
                    )}
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant='outline' size='sm'>
                        <Settings className='w-4 h-4 mr-2' />
                        Configure
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuLabel>Yield Settings</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => openYieldModal(asset)}>
                        <Percent className='w-4 h-4 mr-2' />
                        Update Rate
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openPriceModal(asset)}>
                        <DollarSign className='w-4 h-4 mr-2' />
                        Update Price
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openRewardModal(asset)}>
                        <Play className='w-4 h-4 mr-2' />
                        Add Reward
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Distribution progress bar — distributed out of (distributed + pending) */}
              {distributed + pending > 0 && (
                <div className='mt-4 pt-4 border-t border-border'>
                  <div className='flex items-center justify-between text-sm mb-2'>
                    <span className='text-muted-foreground'>Yield claimed</span>
                    <span className='text-warning font-medium'>
                      ${pending.toLocaleString(undefined, { maximumFractionDigits: 0 })} pending
                    </span>
                  </div>
                  <Progress value={progressPercentage} className='h-1.5' />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Update Yield Modal */}
      <Dialog open={yieldModalOpen} onOpenChange={setYieldModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Yield Rate</DialogTitle>
            <DialogDescription>
              Update the annual yield percentage for {selectedAssetData?.assetName}
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div>
              <label className='text-sm font-medium'>Current Yield</label>
              <p className='text-2xl font-bold text-success'>
                {ensureNumber(selectedAssetData?.rate).toFixed(1)}%
              </p>
            </div>
            <div>
              <label className='text-sm font-medium mb-2 block'>New Yield Percentage</label>
              <Input
                type='number'
                min={0}
                max={100}
                placeholder='Enter yield percentage (0-100)'
                value={yieldValue}
                onChange={(e) => setYieldValue(e.target.value)}
                disabled={transactionLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setYieldModalOpen(false)}
              disabled={transactionLoading}
            >
              Cancel
            </Button>
            <Button onClick={updateYield} disabled={transactionLoading}>
              {transactionLoading ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Processing...
                </>
              ) : (
                'Update Yield'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Price Modal */}
      <Dialog open={priceModalOpen} onOpenChange={setPriceModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Asset Price</DialogTitle>
            <DialogDescription>
              Update the price for {selectedAssetData?.assetName}
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div>
              <label className='text-sm font-medium'>Current Price</label>
              <p className='text-2xl font-bold text-foreground'>
                ${Number(selectedAssetData?.currentPrice || 0).toFixed(2)} USDT
              </p>
            </div>
            <div>
              <label className='text-sm font-medium mb-2 block'>New Price (USDT)</label>
              <Input
                type='number'
                min={0}
                placeholder='Enter new price in USDT'
                value={priceValue}
                onChange={(e) => setPriceValue(e.target.value)}
                disabled={priceTransactionLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setPriceModalOpen(false)}
              disabled={priceTransactionLoading}
            >
              Cancel
            </Button>
            <Button onClick={updatePrice} disabled={priceTransactionLoading}>
              {priceTransactionLoading ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Processing...
                </>
              ) : (
                'Update Price'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Reward Modal */}
      <Dialog open={rewardModalOpen} onOpenChange={setRewardModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Yield Reward</DialogTitle>
            <DialogDescription>
              Add USDT tokens to the reward pool for {selectedAssetData?.assetName}
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div>
              <label className='text-sm font-medium'>Current Reward Balance</label>
              <p className='text-2xl font-bold text-warning'>
                ${ensureNumber(selectedAssetData?.rewardBalance).toFixed(0)} USDT
              </p>
            </div>
            <div>
              <label className='text-sm font-medium mb-2 block'>Amount to Add (USDT)</label>
              <Input
                type='number'
                min={0}
                placeholder='Enter reward amount in USDT'
                value={rewardValue}
                onChange={(e) => setRewardValue(e.target.value)}
                disabled={rewardTransactionLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setRewardModalOpen(false)}
              disabled={rewardTransactionLoading}
            >
              Cancel
            </Button>
            <Button onClick={addReward} disabled={rewardTransactionLoading}>
              {rewardTransactionLoading ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Processing...
                </>
              ) : (
                'Add Reward'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UpdateYields;
